import services from '../services/api';
import { onlyNumbers } from '../utils/normalize';

// Dados da area da oficina. Toda rota aqui exige a sessao da oficina (cookie) e a API
// confere que o sourceId/companyId do path sao da propria empresa.
const api = services.apiV1;

export const SOURCE = { QUERIDO_CARRO: 1, OS_DIGITAL: 2 };
export const MAX_PHOTOS = 5;

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

// O Querido Carro manda 1/0; o OS Digital, true/false.
const toBool = (value) => value === true || value === 1 || value === '1';

const companyPath = (company) => `${company.sourceId}/${company.id}`;

// --- Avaliacoes ---

export function normalizeEvaluation(raw, index) {
  const ratingProduct = toNumber(raw.ratingProduct);
  const ratingPrice = toNumber(raw.ratingPrice);
  return {
    key: `${raw.id ?? raw.code ?? 'ev'}-${index}`,
    id: raw.id,
    code: raw.code,
    sourceId: toNumber(raw.sourceId),
    // Querido Carro nao informa o tipo ("-").
    type: raw.type && raw.type !== '-' ? raw.type : null,
    date: raw.date || null,
    client: String(raw.client || '').trim() || 'Cliente',
    plate: String(raw.vehicle || '').trim(),
    value: raw.value === null || raw.value === undefined ? null : toNumber(raw.value),
    indicate: toBool(raw.indicate),
    ratingProduct,
    ratingPrice,
    average: (ratingProduct + ratingPrice) / 2,
    comment: String(raw.comments || '').trim(),
    companyCnpj: raw.cpfCnpj || '',
  };
}

const byDateDesc = (a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0);

export async function loadEvaluations(company) {
  const { data } = await api.get(`satisfaction-survey/by-source/${companyPath(company)}`);
  return (Array.isArray(data) ? data : []).map(normalizeEvaluation).sort(byDateDesc);
}

// Busca por cliente ou placa, sem acento e sem diferenciar maiusculas.
const fold = (value) => String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function filterEvaluations(list, { query = '', indicate = 'all', type = 'all' } = {}) {
  const term = fold(query).trim();
  const plateTerm = term.replace(/[^a-z0-9]/g, '');
  return list.filter((item) => {
    if (indicate === 'yes' && !item.indicate) return false;
    if (indicate === 'no' && item.indicate) return false;
    if (type !== 'all' && item.type !== type) return false;
    if (!term) return true;
    return fold(item.client).includes(term) || (plateTerm && fold(item.plate).replace(/[^a-z0-9]/g, '').includes(plateTerm));
  });
}

// Itens do atendimento avaliado: rota da oficina (a de manutencoes e so do cliente).
export async function loadEvaluationItems(company, evaluation) {
  const { data } = await api.get(`satisfaction-survey/by-source/${companyPath(company)}/items`, {
    params: { saleId: evaluation.id, code: evaluation.code },
  });
  return (Array.isArray(data) ? data : []).map((item) => ({
    description: String(item.description || '').trim(),
    quantity: toNumber(item.quantity),
    unitValue: toNumber(item.unitValue),
    total: toNumber(item.total),
  }));
}

// --- Dados da oficina e fotos ---

const EMPTY_SLOT = null;

export function normalizeMyData(raw = {}) {
  const phones = Array.isArray(raw.Phones) ? raw.Phones : [];
  const phone = phones[0] && phones[0].ddd && phones[0].number ? `${phones[0].ddd}${phones[0].number}` : '';
  const files = Array.isArray(raw.Files) ? raw.Files : [];
  const photos = files.slice(0, MAX_PHOTOS).map((file) => ({ id: file.id, url: file.file || file.url || file.URL, name: file.name }));
  return {
    form: {
      name: raw.name || '',
      tradingName: raw.tradingName || '',
      email: raw.email || '',
      phone: onlyNumbers(phone),
      zipcode: onlyNumbers(raw.zipcode || ''),
      street: raw.street || '',
      streetNumber: raw.streetNumber ? String(raw.streetNumber) : '',
      complement: raw.complement || '',
      neighborhood: raw.neighborhood || '',
      city: raw.city || '',
      state: raw.state || '',
    },
    cpfCnpj: raw.cpfCnpj || '',
    phoneId: phones[0] ? phones[0].id : null,
    logo: raw.logo || null,
    photos: [...photos, ...Array(MAX_PHOTOS - photos.length).fill(EMPTY_SLOT)],
  };
}

export async function loadMyData(company) {
  const { data } = await api.get(`companies/get-my-data/${companyPath(company)}`);
  return normalizeMyData(data);
}

export const REQUIRED_FIELDS = ['name', 'tradingName', 'email', 'phone', 'zipcode', 'street', 'streetNumber', 'neighborhood', 'city', 'state'];

export function missingFields(form) {
  return REQUIRED_FIELDS.filter((field) => !String(form[field] || '').trim());
}

export async function saveMyData(company, myData, form) {
  await api.put(`companies/${companyPath(company)}`, {
    ...form,
    zipcode: onlyNumbers(form.zipcode),
    phone: onlyNumbers(form.phone),
    phoneId: myData.phoneId,
  });
}

export async function fetchCep(zipcode) {
  const digits = onlyNumbers(zipcode);
  if (digits.length !== 8) return null;
  const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
  const data = await response.json();
  if (!data || data.erro) return null;
  return { street: data.logradouro || '', neighborhood: data.bairro || '', city: data.localidade || '', state: data.uf || '' };
}

// Nome do objeto no bucket (o que vem depois do dominio da AWS).
export const objectName = (url) => {
  const match = /amazonaws\.com\/(.+)$/.exec(String(url || ''));
  return match ? decodeURIComponent(match[1]) : null;
};

const randomId = () => Math.random().toString(16).slice(2, 10) + Date.now().toString(16);

async function uploadImage(file) {
  const form = new FormData();
  form.append('image', file, `${randomId()}-${file.name}`);
  const { data } = await api.post('s3', form);
  return data.imageURL;
}

/**
 * Salva logo e fotos. `logo` e cada slot de `photos`: null (vazio), { url, id } (ja salvo)
 * ou { file } (novo). `original` e o estado carregado, para saber o que saiu.
 */
export async function saveImages(company, original, { logo, photos }) {
  const newLogoUrl = logo && logo.file ? await uploadImage(logo.file) : (logo && logo.url) || '';

  const imagesToInsert = [];
  for (const slot of photos) {
    if (slot && slot.file) {
      const url = await uploadImage(slot.file);
      imagesToInsert.push({ URL: url, name: objectName(url) || slot.file.name });
    }
  }

  const keptIds = new Set(photos.filter((slot) => slot && slot.id !== undefined && !slot.file).map((slot) => String(slot.id)));
  const imagesToDelete = original.photos
    .filter((slot) => slot && !keptIds.has(String(slot.id)))
    .map((slot) => ({ filesId: slot.id, id: slot.id, name: slot.name, file: slot.url }));

  await api.put(`companies/my-personal-data-files/${companyPath(company)}`, {
    imagesToInsert,
    imagesToDelete,
    logo: newLogoUrl,
  });

  // O logo antigo sai do bucket; falhar aqui nao desfaz o que ja foi salvo.
  if (original.logo && original.logo !== newLogoUrl && objectName(original.logo)) {
    try {
      await api.post('s3/delete', { images: [{ name: objectName(original.logo) }] });
    } catch (err) {
      // Logo antigo sem dono verificavel (raiz do bucket) fica no bucket, como antes.
    }
  }
}

// --- Atendimento (segmentos, ramo, servicos, horario, site) ---

const toHour = (value) => {
  const digits = onlyNumbers(value || '');
  return digits.length >= 4 ? `${digits.slice(0, 2)}:${digits.slice(2, 4)}` : '';
};

const ids = (list) => (Array.isArray(list) ? list.map((item) => Number(item.id)).filter(Number.isFinite) : []);

export function normalizeConfig(raw = {}) {
  return {
    segments: ids(raw.Segments),
    services: ids(raw.Services),
    branchId: raw.branchId ? Number(raw.branchId) : null,
    openAt: toHour(raw.openAt),
    closesAt: toHour(raw.closesAt),
    site: raw.site || '',
  };
}

export async function loadConfigOptions() {
  const [segments, branches, servicesList] = await Promise.all([
    api.get('segments'), api.get('branches'), api.get('services'),
  ]);
  return { segments: segments.data || [], branches: branches.data || [], services: servicesList.data || [] };
}

export async function loadConfig(company) {
  const { data } = await api.get(`companies/config-data/${companyPath(company)}`);
  return normalizeConfig(data);
}

// Ramo 1 (autopecas) nao escolhe servicos.
export const branchHasServices = (branchId) => Boolean(branchId) && Number(branchId) !== 1;

const isValidHour = (value) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

export function validateConfig(config) {
  if (!config.segments.length) return 'Escolha ao menos um tipo de veículo.';
  if (!config.branchId) return 'Escolha o ramo de atuação.';
  if (branchHasServices(config.branchId) && !config.services.length) return 'Escolha ao menos um serviço.';
  if (config.openAt || config.closesAt) {
    if (!isValidHour(config.openAt) || !isValidHour(config.closesAt)) return 'Informe o horário no formato 08:00.';
    if (config.openAt >= config.closesAt) return 'O horário de fechamento precisa ser depois da abertura.';
  }
  if (config.site && !/^https?:\/\/[^\s.]+\.[^\s]+$/i.test(config.site)) return 'Informe o site completo, começando com https://';
  return null;
}

export async function saveConfig(company, config) {
  await api.put(`companies/config-data/${companyPath(company)}`, {
    companyId: company.id,
    sourceId: company.sourceId,
    segments: config.segments.map((id) => ({ id })),
    services: branchHasServices(config.branchId) ? config.services.map((id) => ({ id })) : [],
    branchId: config.branchId,
    openAt: config.openAt,
    closesAt: config.closesAt,
    site: config.site,
  });
}
